import { markRaw, shallowRef } from 'vue'
import type { ShallowRef } from 'vue'
import type { InsertPreparationHandle } from './useEditorGlbLoader'

/**
 * 当前待放置插入句柄的 Vue 引用类型。
 *
 * 设计说明：
 * 1. InsertPreparationHandle 内部持有 LoadedImportedModel，最终会包含 THREE.Object3D；
 * 2. 该引用必须保持 shallow，禁止 Vue 深代理 Three.js 对象；
 * 3. 统一类型别名可以让取消工作流继续按 Ref 语义消费，同时明确这里的特殊生命周期要求。
 */
export type PendingInsertionHandleRef = ShallowRef<InsertPreparationHandle | null>

/**
 * 创建待放置组件句柄引用，供文件插入与装配引用复制共用。
 *
 * @typeParam T 句柄类型，默认保持原文件插入调用契约。
 * @returns 不会深度代理句柄内容的浅层引用。
 */
export function createPendingInsertionHandleRef<T extends object = InsertPreparationHandle>(): ShallowRef<T | null> {
  // 只追踪句柄替换。引用复制也包含源 Mesh、副本和 WeakMap，必须保留这些对象的引用身份。
  return shallowRef<T | null>(null)
}

/**
 * 写入待放置组件句柄，禁止在 UI 状态边界代理 SDK 的运行时对象。
 *
 * @typeParam T 文件插入或引用复制句柄类型。
 * @param target 待写入的浅层句柄引用。
 * @param handle 新句柄；传入 null 表示清空。
 */
export function setPendingInsertionHandle<T extends object>(
  target: ShallowRef<T | null>,
  handle: T | null,
): void {
  // 句柄作为整体进入其他响应式状态时仍保持原始引用；不允许下游靠 toRaw 补救 WeakMap 查询。
  target.value = handle ? markRaw(handle) : null
}
